import { type FeatureDefinition, GrowthBook, type StickyBucketServiceSync } from '@growthbook/growthbook'
import { shallowRef } from 'vue'
import { getCookie, setCookie } from '@/js/services/Utils'
import { CustomerTrack, getCustomerTrackData } from '@/js/helpers/track-config'
import type { IRogue } from '@/js/services/RogueTypes'
import type { AppFeatures, AppFeatureKey } from '@/js/services/types/features'

/**
 * Anonymous visitor id, minted by the browser on first use (see getHashId).
 * Nothing sets it server-side. Sticky bucket cookies are keyed by this value
 * (`gbStickyBuckets__anonymous_id||<tracking_id>`), so renaming it would orphan
 * every stored assignment — hence the constant rather than a literal.
 */
const TRACKING_COOKIE = 'tracking_id'

const TRACKING_EXP_DAYS = 365

/**
 * Random, well-distributed id. Growthbook hashes it to pick a bucket, so it only
 * needs spread, not unpredictability — `Math.random` suffices and keeps this
 * isomorphic (no node `crypto`, no Web Crypto feature detection).
 */
function randomId(): string {
  return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

export class FeatureFlags {
  rogue: IRogue

  private service: GrowthBook<AppFeatures>

  private stickyBuckets?: StickyBucketServiceSync

  // Non-zero while a sticky bucket refresh is in flight. Growthbook applies new
  // attributes synchronously but reloads the sticky assignment docs behind an
  // await, so an evaluation in the same tick would use the previous user's docs.
  private stickyPending = 0

  // Settles when the newest sticky bucket reload finishes.
  private stickyRefresh: Promise<void> = Promise.resolve()

  // Generated once per instance — one per render on SSR — so every getBaseAttrs()
  // call within a single render agrees on the bucket. Prefixed to make it obvious
  // in a debugger that it is not a visitor id. Unused in the browser.
  private ssrHashId = `ssr-${randomId()}`

  // Bumped each time a new payload is applied. eval() reads it, so computeds and watchers
  // that evaluate a flag re-run when a heartbeat brings new values.
  private payloadVersion = shallowRef(0)

  // Growthbook dateUpdated of the applied payload, used to skip heartbeats that repeat it
  featuresUpdatedDate?: string

  force: { [key: string]: unknown } = {}

  constructor(
    rogue: IRogue,
    features: Record<string, FeatureDefinition>,
    force: { [key: string]: any } = {},
    stickyBuckets?: StickyBucketServiceSync,
    featuresUpdatedDate?: string
  ) {
    this.rogue = rogue
    this.stickyBuckets = stickyBuckets
    this.featuresUpdatedDate = featuresUpdatedDate

    this.service = new GrowthBook({
      // Registers the instance for the Growthbook DevTools browser extension
      // in every environment except production. The extension is browser-only,
      // so SSR instances never enable it.
      enableDevMode       : !rogue.ssr && (rogue.env !== 'prod' || import.meta.env.DEV),
      // Give Growthbook the request URL so its built-in query-string override
      // (`?<experimentKey>=<variationIndex>`) is honored. On the client Growthbook
      // would otherwise fall back to window.location.href, but on SSR the context
      // url is empty — so without this the override only takes on the client and
      // diverges from the SSR-rendered bucket. initialRoute carries the query
      // string in both contexts (renderUrl on SSR, window.location on the client),
      // keeping the forced variation consistent across render and hydration.
      url                 : rogue.initialRoute,
      attributes          : this.getBaseAttrs(),
      // Holds a visitor on the variation they first saw when an experiment's split
      // or targeting changes under them. Does NOT survive a change to the hash
      // attribute's value (e.g. _id at sign-in): docs are keyed by that value, so
      // a new one misses the lookup. Browser only — undefined on SSR. Also needs
      // sticky bucketing enabled org-wide in Growthbook to have any effect.
      stickyBucketService : stickyBuckets,
      trackingCallback    : (experiment, result) => {
        rogue.track.track({ type: 'ab', experiment: experiment.key, variationId: result.variationId })
      },
    })

    // Features are already in hand here (SSR state on the client, heartbeat data
    // on the server), so hydrate synchronously — initSync replaces the deprecated
    // loadFeatures() flow. It loads sticky assignment docs on the way through,
    // which is why the service must be sync.
    this.service.initSync({ payload: { features } })

    this.force = force
  }

  /**
   * Applies attributes, tracking the sticky bucket reload they trigger. With a
   * service configured, `setAttributes` refreshes the assignment docs behind an
   * await, so `stickyPending` lets callers hold off evaluating until they land.
   * Without one there is nothing to await and this is a pass-through.
   */
  private setAttrs(attributes: Record<string, unknown>): void {
    const applied = this.service.setAttributes(attributes)

    if (!this.stickyBuckets || !applied?.then) {
      return
    }

    this.stickyPending++
    const done = () => { this.stickyPending-- }
    this.stickyRefresh = applied.then(done, done)
  }

  /** False while attribute changes are still propagating into the sticky bucket docs. */
  private get stickyBucketsReady(): boolean {
    return this.stickyPending === 0
  }

  /** Runs `fn` once the in-flight sticky bucket reload has settled. */
  private whenStickyBucketsReady(fn: () => void): void {
    this.stickyRefresh.then(fn, fn)
  }

  /**
   * Value behind the `_id` / `anonymous_id` hash attributes.
   *
   * SSR ignores the visitor's tracking_id: renders are CDN-cached and shared, so
   * bucketing on the requester bakes one visitor's variation into HTML served to
   * everyone. Sending no id is worse — an empty hash value excludes the render
   * from every experiment, leaving the cached corpus all control — so each render
   * gets a fresh id and the corpus spreads across variations by their weights.
   * Exposures are unaffected: analytics no-ops on SSR, so only the browser (which
   * re-buckets on the real id at hydration) reports an assignment.
   *
   * The browser owns the visitor id outright — nothing mints it server-side, where
   * a Set-Cookie would ride a publicly cached response and the origin is skipped
   * on a cache hit anyway, leaving most visitors without one.
   */
  private getHashId(): string | null | undefined {
    if (this.rogue.ssr) {
      return this.ssrHashId
    }

    const existing = getCookie(TRACKING_COOKIE)
    if (existing) {
      return existing
    }

    setCookie(TRACKING_COOKIE, randomId(), TRACKING_EXP_DAYS)

    // Return what actually persisted, never the value just generated. With cookies
    // blocked the read comes back empty and the visitor stays unbucketed — which
    // is what we want, because a fresh id on every call would flip them between
    // arms within one page and report exposures for both.
    return getCookie(TRACKING_COOKIE)
  }

  getBaseAttrs() {
    const qs = (this.rogue.initialRoute || '').indexOf('?') !== -1
      ? this.rogue.initialRoute.split('?')[1].replace(/=/g, '').split('&')
      : []

    const trackingId = this.getHashId()

    return {
      store : this.rogue.site.code,
      ir    : this.rogue.initialRoute.replace(this.rogue.localizedBaseURL, ''),
      qs,
      ...(trackingId ? { _id: trackingId, anonymous_id: trackingId } : {}),
    }
  }

  eval<K extends AppFeatureKey>(feature: K, defaultVal: AppFeatures[K] | null = null): AppFeatures[K] | null {
    // Read for dependency tracking only
    void this.payloadVersion.value

    const forced = this.force[feature]
    if (forced === undefined) {
      return this.service.getFeatureValue(feature, defaultVal as AppFeatures[K]) as AppFeatures[K] | null
    }

    // Growthbook doesn't track experiments if we are returning a forced value so we need to do it ourselves...
    if (!this.rogue.ssr) {
      const variations = this.service.getFeatures()[feature]?.rules?.find(r => r.variations)?.variations
      const variationId = variations?.indexOf(forced)
      if (variationId && variationId !== -1) {
        this.rogue.track.track({ type: 'ab', experiment: feature, variationId })
      }
    }

    return forced as AppFeatures[K]
  }

  /**
   * Shortcut to check if a feature is true, vs false / null / undefined
   * @param feature
   */
  isOn(feature: AppFeatureKey) {
    return Boolean(this.eval(feature))
  }

  /** Returns the Constructor AA/BB test cell for the current user, or null if not in an experiment. */
  getConstructorTestCell(): string | null {
    return this.cellFromEvalResult(this.service.evalFeature('enable_constructor'))
  }

  // Derives the test-cell name from one evalFeature result, so callers can evaluate the feature a single time.
  private cellFromEvalResult(result: any): string | null {
    if (result.source !== 'experiment') return null

    const variationId = result.experimentResult?.variationId ?? 0
    const numVariations = result.experiment?.variations?.length ?? 2

    if (numVariations === 4) {
      const aabbCells = ['control_1', 'control_2', 'constructor_1', 'constructor_2']

      return aabbCells[variationId] ?? null
    }

    return result.value ? 'constructor' : 'control'
  }

  // Writes window.cnstrc.testCell from the experiment bucket; returns true once resolved (cell set or feature forced), false while bucketing attributes aren't ready yet.
  syncConstructorTestCell(): boolean {
    if (this.rogue.ssr) return true

    const w = window as any
    if (!w.cnstrc) return false
    if (w.cnstrc.testCell) return true

    // Feed tracking_id into Growthbook the moment the analytics tag creates it, so we bucket on page 1 (not page 2) on the same id. 
    // Never arrives in the window → stays unbucketed (no fallback id).
    const trackingId = getCookie(TRACKING_COOKIE)

    if (trackingId) {
      const attrs = this.service.getAttributes()

      // Set the hash attr anonymous_id; seed _id to match getBaseAttrs, but never overwrite an existing _id (e.g. customer._id).
      if (attrs.anonymous_id !== trackingId) {
        this.setAttrs({ ...attrs, anonymous_id: trackingId, ...(attrs._id ? {} : { _id: trackingId }) })
      }
    }

    // The sticky bucket docs for the id we just set land a microtask later, so
    // evaluating now would bucket off the previous id's assignments. Report
    // unresolved and let the retry loop pick it up on the next tick.
    if (!this.stickyBucketsReady) {
      return false
    }

    // Evaluate once so the cell name and the force/experiment decision come from the same state.
    const result = this.service.evalFeature('enable_constructor')
    const cell = this.cellFromEvalResult(result)

    if (cell) {
      w.cnstrc.testCell = cell

      return true
    }

    // No cell: stop only if forced (no experiment running); any other source means attributes may still be loading — keep trying.
    return result.source === 'force'
  }

  // Retries until the cell resolves (attributes can lag mount); 10ms/1000 attempts (~10s) mirrors Constructor's own beacon (checkForTestCellInterval).
  ensureConstructorTestCell(attempts = 1000, intervalMs = 10): void {
    if (this.syncConstructorTestCell()) return

    let remaining = attempts
    const tick = () => {
      if (this.syncConstructorTestCell()) return

      if (--remaining <= 0) {
        this.rogue.log.warn('Constructor.io test cell never resolved; running without experiment assignment')

        return
      }

      window.setTimeout(tick, intervalMs)
    }
    window.setTimeout(tick, intervalMs)
  }

  // From heartbeat, update flags observable object to enable/disable things on the site
  updateFeatures(feats?: Record<string, FeatureDefinition>, featuresUpdatedDate?: string) {
    if (!feats) {
      return
    }

    // Most heartbeats repeat the current payload. Re-applying it would re-run every flag
    // computed for nothing. Without a dateUpdated there is nothing to compare, so apply it.
    if (featuresUpdatedDate && featuresUpdatedDate === this.featuresUpdatedDate) {
      return
    }

    this.featuresUpdatedDate = featuresUpdatedDate

    // setPayload reloads the sticky bucket docs too — the new payload can
    // introduce experiments on hash attributes the current docs don't cover.
    // It applies the features behind those awaits, so the bump waits for it to settle.
    const applied = Promise.resolve(this.service.setPayload({ features: feats }))
    const bump = () => { this.payloadVersion.value++ }

    if (this.stickyBuckets) {
      this.stickyPending++
      const done = () => {
        this.stickyPending--
        bump()
      }
      this.stickyRefresh = applied.then(done, done)
    } else {
      applied.then(bump, bump)
    }
  }

  /**
   * Attributes are attributes about the customer and route that might impact which features are active or inactive
   * @param customer
   */
  updateAttributes(customer?: CustomerTrack) {
    const attributes = {
      ...this.getBaseAttrs(),
      route: this.rogue.router?.currentRoute.value.path || this.rogue.initialRoute,
    }

    if (!this.rogue.ssr) {
      customer = customer || getCustomerTrackData(this.rogue)

      if (!customer.loading) {
        // `_id` prefers the customer id so a signed-in visitor buckets the same on
        // every device. The trade: it changes at sign-in, rehashing an _id-hashed
        // experiment mid-session, which sticky bucketing cannot cover (see the
        // stickyBucketService note above). Hashing on user_id with an anonymous_id
        // fallback is what would.
        Object.assign(attributes, {
          _id          : customer._id || getCookie(TRACKING_COOKIE),
          user_id      : customer._id,
          anonymous_id : getCookie(TRACKING_COOKIE),
          email        : customer.email,
          isRogue      : customer.email && customer.email.indexOf('@roguefitness.com') !== -1,
          loggedIn     : customer.loggedIn,
          groupId      : customer.groupId,
        })
      }
    }

    this.setAttrs(attributes)

    // Re-evaluate Constructor test cell now that attributes (tracking_id) are set.
    // Deferred past the sticky bucket reload when one is in flight, so the cell is
    // derived from the assignments belonging to these attributes.
    if (!this.rogue.ssr) {
      if (this.stickyBucketsReady) {
        this.syncConstructorTestCell()
      } else {
        this.whenStickyBucketsReady(() => this.syncConstructorTestCell())
      }
    }

    // this.rogue.log.debug('Tracking attributes', attributes)
  }
}
